import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import TurnstileWrapper from "components/TurnstileWrapper";
import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";
import { TURNSTILE_KEY } from "helpers/constants";

const TurnstileContext = createContext();

export const useTurnstile = () => useContext(TurnstileContext);

export const TurnstileProvider = ({ children }) => {
  const [isTurnstileInteractive, setIsTurnstileInteractive] = useState();
  const [captchaToken, setCaptchaToken] = useState({ token: null });
  const [error, setError] = useState();
  const { translate: t } = useLanguageProvider();
  const turnstileRef = useRef(null);

  const refreshTurnstile = useCallback(() => {
    turnstileRef.current?.reset?.();
  }, []);

  const skeletonMap = {
    "/login": {
      header: "Log in",
      renderComponent: (
        <>
          {["Email", "Password"].map((input) => (
            <div key={input}>
              <label htmlFor="signup_name">{input}</label>
              <div className="form-group turnstile-skeleton">
                <input className="form-control input-text" disabled />
              </div>
            </div>
          ))}
        </>
      ),
    },
    "/signup": {
      header: "Sign up",
      subheader: "Sign up to create a campaign or track your donations",
      renderComponent: (
        <>
          {["Name", "Email", "Password", "Password"].map((input, index) => (
            <div key={`${input}_${index}`}>
              <label htmlFor="signup_name">{input}</label>
              <div className="form-group turnstile-skeleton">
                <input className="form-control input-text" disabled />
              </div>
            </div>
          ))}
        </>
      ),
    },
    "/password/reset": {
      header: "Reset Password",
      subheader: "Enter your email to reset your password",
      renderComponent: (
        <div className="form-group turnstile-skeleton">
          <input className="form-control input-text" disabled />
        </div>
      ),
    },
  };

  // Determine which skeleton to show based on current path
  const skeleton = skeletonMap[location.pathname] || null;

  const value = useMemo(
    () => ({ captchaToken, refreshTurnstile }),
    [captchaToken, refreshTurnstile],
  );

  return (
    <TurnstileContext.Provider value={value}>
      {captchaToken.token ? (
        children
      ) : (
        <div className="form-row w-600">
          <h3 className="login-header">{skeleton?.header}</h3>
          {skeleton.subheader && <p>{skeleton.subheader}</p>}

          {!isTurnstileInteractive && (
            <div className="form">
              {skeleton?.renderComponent}
              <div className="form-group">
                {error ? (
                  <div>
                    {t("We are unable to verify you at this stage. Please try again later.")}
                  </div>
                ) : (
                  <button className="link-button --bold --primary turnstile-skeleton" disabled>
                    {t("Verifying...")}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <div className={`turnstile__wrapper ${captchaToken.token ? "hidden" : ""}`}>
        <TurnstileWrapper
          ref={turnstileRef}
          onChange={(token) => setCaptchaToken({ token })}
          captchaKey={TURNSTILE_KEY}
          setTurnstileInteractive={setIsTurnstileInteractive}
          onError={setError}
          action={`auth_${(location.pathname || "").replace(/[^a-zA-Z0-9]/g, "-")}`}
        />
      </div>
    </TurnstileContext.Provider>
  );
};
